import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import AuthContext from '../../context/AuthContext';
import api from '../../services/api';
import toastr from 'toastr';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import AdminPanel from './AdminPanel';
import './Chat.css';

const HEARTBEAT_MS = 10000;
const TYPING_THROTTLE_MS = 2000;

export default function PrivateChat({ onUnreadChange }) {
    const { user, ws, registerOnWSMessage } = useContext(AuthContext);
    const isAdmin = user?.role === 'admin';

    const [accessState, setAccessState] = useState('loading');
    const [globalMutedUntil, setGlobalMutedUntil] = useState(null);
    const [myMuteUntil, setMyMuteUntil] = useState(null);
    const [historyResetAt, setHistoryResetAt] = useState(null);

    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [pinned, setPinned] = useState(null);
    const [presence, setPresence] = useState([]);
    const [typing, setTyping] = useState([]);
    const [knownUsers, setKnownUsers] = useState([]);
    const [replyTo, setReplyTo] = useState(null);
    const [readersModal, setReadersModal] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [searchQ, setSearchQ] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [asideOpen, setAsideOpen] = useState(false); // мобильный drawer админ-панели

    const messagesContainerRef = useRef(null);
    const lastTypingSentRef = useRef(0);
    const heartbeatTimerRef = useRef(null);
    const typingExpireRef = useRef(new Map()); // login -> timeoutId, safety net 5сек

    const allMessagesById = React.useMemo(() => {
        const m = new Map();
        for (const x of messages) m.set(String(x._id), x);
        if (pinned) m.set(String(pinned._id), pinned);
        return m;
    }, [messages, pinned]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const acc = await api.chat.access();
                if (cancelled) return;
                if (!acc.data.allowed) { setAccessState('denied'); return; }
                setGlobalMutedUntil(acc.data.globalMutedUntil);
                setMyMuteUntil(acc.data.myMuteUntil);
                setHistoryResetAt(acc.data.historyResetAt);
                setAccessState('ready');

                const [hist, pin, users] = await Promise.all([
                    api.chat.messages({ limit: 50 }),
                    api.chat.pinned(),
                    api.chat.usersWithAccess()
                ]);
                if (cancelled) return;
                setMessages(hist.data.messages || []);
                setHasMore(!!hist.data.hasMore);
                setPinned(pin.data.pinned);
                setKnownUsers(users.data.users || []);
                if (isAdmin) {
                    try { const pr = await api.chat.presence(); setPresence(pr.data.present || []); } catch (_) {}
                }
                setTimeout(() => scrollToBottom(true), 50);
                setTimeout(markVisibleRead, 200);
            } catch (e) {
                if (!cancelled) setAccessState('denied');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const scrollToBottom = (instant = false) => {
        const el = messagesContainerRef.current;
        if (!el) return;
        if (instant) el.scrollTop = el.scrollHeight;
        else el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    };

    const markVisibleRead = useCallback(() => {
        if (!user) return;
        const login = user.login;
        const sock = ws?.current;
        if (!sock || sock.readyState !== WebSocket.OPEN) return;
        for (const m of messages) {
            if (m.fromLogin === login) continue;
            if (m.deletedAt) continue;
            const already = (m.readBy || []).some(r => r.userLogin === login);
            if (!already) {
                try { sock.send(JSON.stringify({ action: 'chat:read', messageId: m._id })); } catch (_) {}
            }
        }
        if (onUnreadChange) onUnreadChange(0);
    }, [messages, user, ws, onUnreadChange]);

    useEffect(() => {
        if (accessState !== 'ready') return;
        const sendHb = () => {
            const sock = ws?.current;
            if (sock && sock.readyState === WebSocket.OPEN) {
                try { sock.send(JSON.stringify({ action: 'chat:heartbeat' })); } catch (_) {}
            }
        };
        sendHb();
        heartbeatTimerRef.current = setInterval(sendHb, HEARTBEAT_MS);
        return () => {
            clearInterval(heartbeatTimerRef.current);
            const sock = ws?.current;
            if (sock && sock.readyState === WebSocket.OPEN) {
                try { sock.send(JSON.stringify({ action: 'chat:leave' })); } catch (_) {}
            }
        };
    }, [accessState, ws]);

    useEffect(() => {
        if (accessState !== 'ready') return;
        const off = registerOnWSMessage((msg) => {
            if (!msg || typeof msg.action !== 'string' || !msg.action.startsWith('chat:')) return;

            switch (msg.action) {
                case 'chat:new_message': {
                    setMessages(prev => {
                        if (prev.some(p => String(p._id) === String(msg.message._id))) return prev;
                        return [...prev, msg.message];
                    });
                    setTimeout(() => {
                        const el = messagesContainerRef.current;
                        if (!el) return;
                        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
                        if (nearBottom || msg.message.fromLogin === user.login) scrollToBottom();
                    }, 30);
                    if (msg.message.fromLogin !== user.login) {
                        setTimeout(markVisibleRead, 100);
                    }
                    break;
                }
                case 'chat:message_edited': {
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.message._id) ? msg.message : p));
                    if (pinned && String(pinned._id) === String(msg.message._id)) setPinned(msg.message);
                    break;
                }
                case 'chat:message_deleted': {
                    setMessages(prev => prev.filter(p => String(p._id) !== String(msg.messageId)));
                    if (pinned && String(pinned._id) === String(msg.messageId)) setPinned(null);
                    break;
                }
                case 'chat:message_pinned': {
                    setPinned(msg.message);
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.message._id) ? { ...p, pinned: true } : { ...p, pinned: false }));
                    break;
                }
                case 'chat:message_unpinned': {
                    setPinned(null);
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.messageId) ? { ...p, pinned: false } : p));
                    break;
                }
                case 'chat:read_update': {
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.messageId) ? { ...p, readBy: msg.readBy } : p));
                    break;
                }
                case 'chat:reaction_update': {
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.messageId) ? { ...p, reactions: msg.reactions } : p));
                    break;
                }
                case 'chat:poll_update': {
                    setMessages(prev => prev.map(p => String(p._id) === String(msg.messageId) ? { ...p, poll: msg.poll } : p));
                    break;
                }
                case 'chat:presence_update': {
                    if (isAdmin) setPresence(msg.present || []);
                    break;
                }
                case 'chat:typing_update': {
                    const incoming = (msg.typing || []).filter(l => l !== user.login);
                    setTyping(incoming);
                    // Safety net: если по какой-то причине сервер не пришлёт обновление,
                    // забываем юзера локально через 5 сек.
                    const map = typingExpireRef.current;
                    for (const [login, tid] of map.entries()) {
                        if (!incoming.includes(login)) { clearTimeout(tid); map.delete(login); }
                    }
                    for (const login of incoming) {
                        if (map.has(login)) clearTimeout(map.get(login));
                        const tid = setTimeout(() => {
                            map.delete(login);
                            setTyping(prev => prev.filter(l => l !== login));
                        }, 5000);
                        map.set(login, tid);
                    }
                    break;
                }
                case 'chat:mute_changed': {
                    if (msg.scope === 'global') setGlobalMutedUntil(msg.until || null);
                    else if (msg.scope === 'user' && msg.login === user.login) setMyMuteUntil(msg.until || null);
                    break;
                }
                case 'chat:history_cleared': {
                    setMessages([]); setPinned(null); setHistoryResetAt(msg.at || new Date());
                    toastr.info(' ', 'История чата была очищена администратором.');
                    break;
                }
                default: break;
            }
        });
        return off;
    }, [accessState, registerOnWSMessage, user, pinned, isAdmin, markVisibleRead]);

    const loadOlder = useCallback(async () => {
        if (!hasMore || messages.length === 0) return;
        const oldest = messages[0];
        try {
            const r = await api.chat.messages({ before: oldest._id, limit: 50 });
            const more = r.data.messages || [];
            if (more.length === 0) { setHasMore(false); return; }
            const el = messagesContainerRef.current;
            // Фикс bounce: сохраняем высоту ДО вставки и восстанавливаем позицию ПОСЛЕ рендера
            const prevScrollHeight = el ? el.scrollHeight : 0;
            const prevScrollTop = el ? el.scrollTop : 0;
            setMessages(prev => [...more, ...prev]);
            setHasMore(!!r.data.hasMore);
            // requestAnimationFrame гарантирует что React уже отрендерил новые элементы
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (el) el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
                });
            });
        } catch (_) {}
    }, [hasMore, messages]);

    const onScroll = (e) => {
        if (e.target.scrollTop < 80) loadOlder();
    };

    const sendTyping = () => {
        const now = Date.now();
        if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
        lastTypingSentRef.current = now;
        const sock = ws?.current;
        if (sock && sock.readyState === WebSocket.OPEN) {
            try { sock.send(JSON.stringify({ action: 'chat:typing' })); } catch (_) {}
        }
    };
    const sendTypingStop = useCallback(() => {
        lastTypingSentRef.current = 0;
        const sock = ws?.current;
        if (sock && sock.readyState === WebSocket.OPEN) {
            try { sock.send(JSON.stringify({ action: 'chat:typing_stop' })); } catch (_) {}
        }
    }, [ws]);

    const handlePin = async (id) => { try { await api.chat.pin(id); } catch (e) { toastr.warning(' ', e.response?.data?.message || 'Ошибка'); } };
    const handleDelete = async (id) => { if (!window.confirm('Удалить сообщение для всех?')) return; try { await api.chat.deleteMsg(id); } catch (e) { toastr.warning(' ', e.response?.data?.message || 'Ошибка'); } };
    const handleEdit = async (id, text) => { try { await api.chat.edit(id, text); } catch (e) { toastr.warning(' ', e.response?.data?.message || 'Ошибка'); } };
    const handleShowReaders = async (id) => {
        try { const r = await api.chat.readers(id); setReadersModal({ id, ...r.data }); }
        catch (e) { toastr.warning(' ', 'Не удалось загрузить.'); }
    };
    const handleClearAll = async () => { try { await api.chat.clearAll(); } catch (e) { toastr.warning(' ', e.response?.data?.message || 'Ошибка'); } };
    const refreshPresence = async () => { try { const r = await api.chat.presence(); setPresence(r.data.present || []); } catch (_) {} };

    const scrollToReply = (id) => {
        const el = document.querySelector(`[data-msg-id="${id}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.outline = '2px solid #ffd166';
            setTimeout(() => { el.style.outline = ''; }, 1500);
        }
    };

    const runSearch = async () => {
        const q = searchQ.trim();
        if (!q) { setSearchResults(null); return; }
        try { const r = await api.chat.search(q); setSearchResults(r.data.messages || []); }
        catch (_) { setSearchResults([]); }
    };

    if (accessState === 'loading') {
        return <div className="pchat-gate"><h3>⏳</h3><p>Проверяем доступ…</p></div>;
    }
    if (accessState === 'denied') {
        return (
            <div className="pchat-gate">
                <h3>🔒 Приватный чат недоступен</h3>
                <p>Доступ к приватному чату разрешён только утверждением администратора.</p>
            </div>
        );
    }

    const muted = (globalMutedUntil && new Date(globalMutedUntil) > new Date()) ||
                  (myMuteUntil && new Date(myMuteUntil) > new Date());
    const muteReason = (globalMutedUntil && new Date(globalMutedUntil) > new Date())
        ? `Отправка временно отключена администратором до ${new Date(globalMutedUntil).toLocaleString('ru-RU')}`
        : myMuteUntil && new Date(myMuteUntil) > new Date()
            ? `Вы заглушены до ${new Date(myMuteUntil).toLocaleString('ru-RU')}`
            : null;

    return (
        <div className="pchat-root">
            <div className="pchat-header">
                <span className="pchat-title">💬 Приватный чат</span>
                {isAdmin && (
                    <button
                        className="pchat-aside-toggle"
                        onClick={() => setAsideOpen(true)}
                        title="Админ-панель"
                    >☰ Админ</button>
                )}
                <div className="pchat-search">
                    <input
                        placeholder="Поиск по истории…"
                        value={searchQ}
                        onChange={e => setSearchQ(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') runSearch();
                            if (e.key === 'Escape') { setSearchQ(''); setSearchResults(null); }
                        }}
                    />
                    <button className="pchat-btn" onClick={runSearch} title="Найти">🔍</button>
                    {searchResults && (
                        <div className="pchat-search-results">
                            {searchResults.length === 0 && <div style={{ padding: 12, color: '#888' }}>Ничего не найдено.</div>}
                            {searchResults.map(m => (
                                <div
                                    key={m._id}
                                    className="pchat-search-item"
                                    onClick={() => { scrollToReply(m._id); setSearchResults(null); setSearchQ(''); }}
                                >
                                    <div className="pchat-search-meta">
                                        {m.fromLogin} · {new Date(m.createdAt).toLocaleString('ru-RU')}
                                    </div>
                                    <div>{(m.text || '').slice(0, 100)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {pinned && (
                <div className="pchat-pinned" onClick={() => scrollToReply(pinned._id)}>
                    <span className="pchat-pinned-icon">📌</span>
                    <span><strong>{pinned.fromLogin}:</strong> {(pinned.text || '[вложение]').slice(0, 120)}</span>
                </div>
            )}
            {muteReason && <div className="pchat-mute">⛔ {muteReason}</div>}

            <div className="pchat-body">
                <div className="pchat-messages" ref={messagesContainerRef} onScroll={onScroll} onClick={markVisibleRead}>
                    {hasMore && <div className="pchat-loadmore">↑ прокрутите чтобы загрузить старые сообщения</div>}
                    {messages.map(m => (
                        <MessageItem
                            key={m._id}
                            message={m}
                            currentLogin={user.login}
                            isAdmin={isAdmin}
                            onReply={setReplyTo}
                            onShowReaders={handleShowReaders}
                            onPin={handlePin}
                            onDelete={handleDelete}
                            onEdit={handleEdit}
                            onShowImage={setLightbox}
                            onScrollToReply={scrollToReply}
                            allMessagesById={allMessagesById}
                        />
                    ))}
                </div>
                {isAdmin && (
                    <>
                        <div
                            className={`pchat-aside-overlay ${asideOpen ? 'open' : ''}`}
                            onClick={() => setAsideOpen(false)}
                        />
                        <AdminPanel
                            presence={presence}
                            globalMutedUntil={globalMutedUntil}
                            knownUsers={knownUsers}
                            onClearAll={handleClearAll}
                            onMuteGlobal={refreshPresence}
                            onMuteUser={refreshPresence}
                            onRefreshPresence={refreshPresence}
                            open={asideOpen}
                            onClose={() => setAsideOpen(false)}
                        />
                    </>
                )}
            </div>

            <div className="pchat-typing">
                {typing.length > 0 && (
                    <span>
                        {typing.slice(0, 3).join(', ')} {typing.length === 1 ? 'печатает' : 'печатают'}
                        <span className="dots" />
                    </span>
                )}
            </div>

            <MessageInput
                disabled={muted && !isAdmin}
                disabledReason={muteReason}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                sendTyping={sendTyping}
                sendTypingStop={sendTypingStop}
                knownUsers={knownUsers}
            />

            {lightbox && (
                <div className="pchat-lightbox" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt="" />
                </div>
            )}

            {readersModal && (
                <div className="pchat-modal-back" onClick={() => setReadersModal(null)}>
                    <div className="pchat-modal" onClick={e => e.stopPropagation()}>
                        <h3>👁 Кто прочитал ({readersModal.readBy?.length || 0})</h3>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                            От: <strong>{readersModal.fromLogin}</strong> · {new Date(readersModal.createdAt).toLocaleString('ru-RU')}
                        </div>
                        {readersModal.readBy?.length === 0 && <div style={{ color: '#888' }}>Никто ещё не прочитал.</div>}
                        <ul className="pchat-readers-list">
                            {readersModal.readBy?.map((r, i) => (
                                <li key={i}><strong>{r.userLogin}</strong> — {new Date(r.readAt).toLocaleString('ru-RU')}</li>
                            ))}
                        </ul>
                        <div className="pchat-modal-actions">
                            <button className="pchat-btn primary" onClick={() => setReadersModal(null)}>Закрыть</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
